import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { WrapperInput } from "src/theme/objects/Forms";
import {
  ErrorMessage,
  SmileIcon,
  SuccessMessage,
  Wrapper,
} from "./addKudosStyled";
import { SendKudosButton } from "src/theme/objects/Buttons";
import { convertToRaw, EditorState } from "draft-js";
import createMentionPlugin, {
  defaultSuggestionsFilter,
} from "draft-js-mention-plugin";
import createEmojiPlugin from "draft-js-emoji-plugin";
import Editor from "draft-js-plugins-editor";
import { Logos } from "src/theme/objects/Logos";
import KudosContext from "src/context";

export const AddKudosComponent: React.FC = () => {
  const Entry = (props) => {
    const {mention, selectMention, searchValue, isFocused, ...parentProps } = props;

    return (
      <div {...parentProps}>
        <div>
          <div className="mentionSuggestionsEntryContainerLeft">
            <img
              src={mention.image_url}
              className="mentionSuggestionsEntryAvatar"
              role="presentation"
            />
          </div>

          <div>
            <div className="mentionSuggestionsEntryName">@{mention.name}</div>
            <div className="mentionSuggestionsDisplayName">
              {mention.display_name}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const mentionPlugin = useMemo(
    (): any =>
      createMentionPlugin({
        mentionPrefix: "@",
        supportWhitespace: true,
      }),
    []
  );

  const emojiPlugin = useMemo(
    () => createEmojiPlugin({ selectButtonContent: "" }),
    []
  );

  const context = useContext(KudosContext)!;
  const [suggestions, setSuggestions] = useState(context.users);
  const [isFocused, setIsFocused] = useState(false);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [usersKudosErrorMessage, setUsersKudosErrorMessage] = useState(false);
  useEffect(() => {
    context.fetchUsers();
  }, []);

  const editor = useRef<any>();

  const { MentionSuggestions } = mentionPlugin;
  const { EmojiSuggestions, EmojiSelect } = emojiPlugin;
  const plugins = [mentionPlugin, emojiPlugin];
  const suggestionsWithoutCurrentUser = suggestions.filter(
    (u) => u.id !== context.profile!.id
  );

  const handleChange = (editorState: EditorState) => {
    const isTextIncludesAt =
      editorState.getCurrentContent().getPlainText().indexOf("@") !== -1;
    setEditorState(editorState);
    if (isTextIncludesAt) {
      setUsersKudosErrorMessage(false);
    }
  };

  const getUsersKudos = () => {
    const usersArr: string[] = [];
    const entityMap = convertToRaw(editorState.getCurrentContent()).entityMap;

    Object.keys(entityMap).forEach((key) => {
      if (entityMap[key].type === "mention") {
        const user: string = entityMap[key].data.mention.id;
        usersArr.push(user);
      }
    });

    return usersArr;
  };

  const messageAfterKudos = useMemo(
    () => context.messages.find((m) => m.type === "afterAddedKudos"),
    [context.messages]
  );

  useEffect(() => {
    if (messageAfterKudos !== undefined) {
      const timeToHide = messageAfterKudos.timeToHide
        ? messageAfterKudos.timeToHide * 1000
        : 3000;
      setTimeout(() => {
        context.removeMessage(messageAfterKudos);
      }, timeToHide);
    }
  }, [messageAfterKudos]);

  const handleAddKudos = () => {
    const usersKudos = getUsersKudos();
    if (usersKudos.length < 1) {
      setUsersKudosErrorMessage(true);
      return;
    }
    context.addKudos({
      description: editor.current.editor.editor.innerHTML,
      recipients: usersKudos,
    });
    setEditorState(EditorState.createEmpty());
  };

  return (
    <Wrapper>
      <Logos />
      <WrapperInput focused={isFocused}>
        <Editor
          editorState={editorState}
          onChange={handleChange}
          ref={editor}
          plugins={plugins}
          placeholder="My kudos goes @..."
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <MentionSuggestions
          onSearchChange={({ value }) => {
            setSuggestions(defaultSuggestionsFilter(value, context.users));
          }}
          suggestions={suggestionsWithoutCurrentUser}
          entryComponent={Entry}
        />
        <EmojiSuggestions />
        <EmojiSelect />
        <ErrorMessage show={usersKudosErrorMessage}>
          Choose who are you giving kudos to
        </ErrorMessage>
        <SendKudosButton onClick={handleAddKudos} />
      </WrapperInput>
      {messageAfterKudos !== undefined && (
        <SuccessMessage>
          {messageAfterKudos.message} <SmileIcon />
        </SuccessMessage>
      )}
    </Wrapper>
  );
};
